import type { DeploymentDataToken } from "@turnstile-portal/turnstile.js";
import { TurnstileFactory } from "@turnstile-portal/turnstile.js";
import type { Address } from "viem";
import type { ConfigData, DeploymentData, KeysData, TokenData } from "../queries/config";

// Global factory instance
let globalFactory: TurnstileFactory | null = null;
let factoryPromise: Promise<TurnstileFactory> | null = null;

// Always use Turnstile config for deployment info
export async function getTurnstileFactory(env: string, customTokens?: Record<string, DeploymentDataToken>) {
  return await TurnstileFactory.fromConfig(env, customTokens);
}

// Initialize global factory (should be called at app startup)
export async function initializeConfig(env = "sandbox", customTokens?: Record<string, DeploymentDataToken>) {
  if (globalFactory) {
    return globalFactory;
  }

  if (!factoryPromise) {
    factoryPromise = getTurnstileFactory(env, customTokens)
      .then((factory) => {
        globalFactory = factory;
        return factory;
      })
      .catch((error) => {
        // Reset factoryPromise on failure so we can retry
        factoryPromise = null;
        throw error;
      });
  }
  return factoryPromise;
}

// Get the global factory (throws if not initialized)
export function getFactory(): TurnstileFactory {
  if (!globalFactory) {
    throw new Error("Config not initialized. Call initializeConfig() first.");
  }
  return globalFactory;
}

// Fetch sandbox keys (only for sandbox/test)
export async function fetchSandboxKeys() {
  const resp = await fetch("https://sandbox.aztec.walletmesh.com/api/v1/turnstile/keys.json");
  if (!resp.ok) throw new Error("Failed to fetch sandbox keys");
  return await resp.json();
}

// Legacy compatibility functions
export async function loadConfig(): Promise<ConfigData> {
  const [factory, keys] = await Promise.all([initializeConfig(), fetchSandboxKeys()]);
  const deployment = factory.getDeploymentData();
  return { deployment, keys };
}

export function getConfig(): ConfigData {
  // Note: keys are not available synchronously, this will throw
  throw new Error("Keys not available synchronously. Use fetchSandboxKeys() for async access.");
}

export function getDeploymentData(): DeploymentData {
  return getFactory().getDeploymentData();
}

export function getKeysData(): KeysData {
  throw new Error("Keys not available synchronously. Use fetchSandboxKeys() for async access.");
}

export function getTokensMap(): Record<string, Address> {
  const deployment = getDeploymentData();
  return Object.fromEntries(
    Object.values(deployment.tokens).map((token: TokenData) => [token.symbol, token.l1Address]),
  ) as Record<string, Address>;
}

export function isConfigLoaded(): boolean {
  return globalFactory !== null;
}

export function clearConfigCache(): void {
  globalFactory = null;
  factoryPromise = null;
}
